import {
    Alert,
    Badge,
    Button,
    Card,
    Flex,
    Group,
    Loader,
    Popover,
    SimpleGrid,
    Stack,
    Text
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { ArrowDropDown, ArrowForward, DateRange } from "@mui/icons-material";
import {
    AutoHeightContainer,
    formatDateRangeLabel,
    formatToDateOnly,
    isSameDateRange,
    useGetOrganizationDashboardQuery
} from "@therapprove/common";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    DASHBOARD_DATE_SHORTCUTS,
    DEFAULT_DASHBOARD_DATE_RANGE
} from "./consts";
import { DashboardDateRange, SummaryMetricCard } from "./types";
import {
    getDashboardActionItems,
    getDashboardMetrics,
    getDashboardSummaryCards
} from "./utils";

export const Dashboard = () => {
    // Hooks
    const navigate = useNavigate();
    const [isDatePickerOpen, { open: openDatePicker, close: closeDatePicker }] =
        useDisclosure(false);
    const [dateRange, setDateRange] = useState<DashboardDateRange>(
        DEFAULT_DASHBOARD_DATE_RANGE
    );

    const dateRangeLabel = useMemo(
        () => formatDateRangeLabel(dateRange[0], dateRange[1], "All Time"),
        [dateRange]
    );

    const handleDateRangeChange = (value: DashboardDateRange) => {
        setDateRange(value);

        if (value[0] && value[1]) {
            closeDatePicker();
        }
    };

    const dashboardQueryVariables = useMemo(
        () => ({
            startDate: dateRange[0]
                ? formatToDateOnly(dateRange[0])
                : undefined,
            endDate: dateRange[1] ? formatToDateOnly(dateRange[1]) : undefined
        }),
        [dateRange]
    );

    // Queries
    const [{ data, fetching, error }] = useGetOrganizationDashboardQuery({
        variables: dashboardQueryVariables,
        requestPolicy: "cache-and-network"
    });

    // Data
    const metrics = useMemo(() => getDashboardMetrics(data), [data]);
    const actionItems = useMemo(
        () => getDashboardActionItems(metrics),
        [metrics]
    );
    const summaryCards = useMemo<SummaryMetricCard[]>(
        () => getDashboardSummaryCards(metrics),
        [metrics]
    );

    return (
        <AutoHeightContainer
            style={{ gap: "1rem", overflowY: "auto", overflowX: "hidden", minWidth: 0 }}
        >
            <Flex justify="flex-end">
                <Popover
                    opened={isDatePickerOpen}
                    onChange={openDatePicker}
                    position="bottom-end"
                    shadow="md"
                    withArrow
                >
                    <Popover.Target>
                        <Button
                            variant="default"
                            leftSection={
                                <DateRange
                                    style={{ fontSize: "1rem" }}
                                />
                            }
                            rightSection={
                                <ArrowDropDown
                                    style={{ fontSize: "1rem" }}
                                />
                            }
                            miw="15rem"
                            onClick={openDatePicker}
                        >
                            {dateRangeLabel}
                        </Button>
                    </Popover.Target>

                    <Popover.Dropdown p="sm">
                        <Flex gap="sm" align="flex-start" wrap="wrap">
                            <Stack
                                gap="xs"
                                style={{ minWidth: "8rem" }}
                            >
                                {DASHBOARD_DATE_SHORTCUTS.map(
                                    (shortcut) => {
                                        const shortcutRange =
                                            shortcut.getValue();

                                        return (
                                            <Button
                                                key={shortcut.label}
                                                size="xs"
                                                variant={
                                                    isSameDateRange(
                                                        dateRange,
                                                        shortcutRange
                                                    )
                                                        ? "filled"
                                                        : "light"
                                                }
                                                justify="flex-start"
                                                onClick={() => {
                                                    setDateRange(
                                                        shortcutRange
                                                    );
                                                    closeDatePicker();
                                                }}
                                            >
                                                {shortcut.label}
                                            </Button>
                                        );
                                    }
                                )}
                            </Stack>

                            <DatePicker
                                type="range"
                                value={dateRange}
                                onChange={(value) =>
                                    handleDateRangeChange(
                                        value as DashboardDateRange
                                    )
                                }
                                maxDate={new Date()}
                            />
                        </Flex>
                    </Popover.Dropdown>
                </Popover>
            </Flex>

            {error && (
                <Alert
                    title="Could not load dashboard metrics"
                    styles={{
                        root: {
                            backgroundColor: "var(--mantine-color-red-0)",
                            border: "1px solid var(--mantine-color-red-2)"
                        },
                        title: {
                            color: "var(--mantine-color-red-8)"
                        },
                        message: {
                            color: "var(--mantine-color-red-7)"
                        }
                    }}
                >
                    {error.message}
                </Alert>
            )}

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
                {summaryCards.map((card) => (
                    <Card
                        key={card.label}
                        variant={
                            card.accent === "secondary"
                                ? "dashboard-summary-secondary"
                                : card.accent === "violet"
                                    ? "dashboard-summary-violet"
                                    : "dashboard-summary-primary"
                        }
                        withBorder
                        radius="md"
                        padding="lg"
                    >
                        <Text variant="dashboard-summary-title">
                            {card.label}
                        </Text>
                        <Text
                            variant={
                                card.accent === "secondary"
                                    ? "dashboard-summary-value-secondary"
                                    : card.accent === "violet"
                                        ? "dashboard-summary-value-violet"
                                        : "dashboard-summary-value-primary"
                            }
                        >
                            {card.value}
                        </Text>
                        <Text variant="dashboard-summary-hint">
                            {card.hint}
                        </Text>
                    </Card>
                ))}
            </SimpleGrid>

            <Stack gap="md">
                <Flex justify="space-between">
                    <Text
                        fw={700}
                        style={{ color: "var(--mantine-color-gray-9)" }}
                    >
                        Action Items
                    </Text>
                    {fetching && (
                        <Flex gap="xs">
                            <Loader
                                size="xs"
                                styles={{
                                    root: {
                                        color: "var(--mantine-color-primary-7)"
                                    }
                                }}
                            />
                            <Text
                                size="xs"
                                style={{
                                    color: "var(--mantine-color-gray-6)"
                                }}
                            >
                                Refreshing
                            </Text>
                        </Flex>
                    )}
                </Flex>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                    {actionItems.map((item) => (
                        <Card
                            key={item.label}
                            variant={
                                item.count > 0
                                    ? "dashboard-action-attention"
                                    : "dashboard-action-idle"
                            }
                            withBorder
                            radius="md"
                            padding="lg"
                        >
                            <Group
                                justify="space-between"
                                align="start"
                                wrap="nowrap"
                            >
                                <Stack gap={4} style={{ flex: 1 }}>
                                    <Text variant="dashboard-action-title">
                                        {item.label}
                                    </Text>
                                    <Text variant="dashboard-action-hint">
                                        {item.hint}
                                    </Text>
                                </Stack>
                                <Badge
                                    variant={
                                        item.count > 0
                                            ? "dashboard-count-attention"
                                            : "dashboard-count-idle"
                                    }
                                >
                                    {item.count}
                                </Badge>
                            </Group>

                            {item.count > 0 && (
                                <Group justify="flex-end" mt="md">
                                    <Button
                                        size="xs"
                                        variant="filled"
                                        rightSection={
                                            <ArrowForward
                                                style={{
                                                    fontSize: "0.9rem"
                                                }}
                                            />
                                        }
                                        onClick={() => navigate(item.path)}
                                    >
                                        Open
                                    </Button>
                                </Group>
                            )}
                        </Card>
                    ))}
                </SimpleGrid>
            </Stack>
        </AutoHeightContainer>
    );
};
